/*
===============================================================================================================
                                 Copyright 2023-2025, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import ClickAway from '@/components/click-away/click-away';
import TextDiv from '@/components/text-div/text-div';
import { LocaleContext } from '@/contexts/locale-context';
import React, {
  createRef, useContext, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import EditableTextInput from '@/components/editable-text-input/editable-text-input';
import EditableDropdown from '@/components/editable-dropdown/editable-dropdown';
import { getStaticCdnPath } from '@/utilities/static-build';

/**
 * Component that allows user to select the type of a custom attribute
 * @param {string} inputSelectorId - DOM id, used to identify this element
 * @param {func} onChange - Called when selection changes
 * @param {string} placeholder - Placeholder text
 * @param {string} defaultSelectedOption - Currently selected variant
 * @param {Array} options - List of custom attribute variants availbale for selection
 * @param {boolean} editMode - Whether or not the component is in edit mode
 * @param {shape} selectedVariantProps - The extra props passed to a selected attribute type
 */
const CustomAttributeSelector = ({
  inputSelectorId,
  options,
  placeholder,
  defaultSelectedOption,
  onChange,
  editMode,
  selectedVariantProps,
}) => {
  const { translate } = useContext(LocaleContext);
  const [selectedInput, setSelectedInput] = useState(null);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [focusedItemIndex, setFocusedItemIndex] = useState(null);

  const itemRefs = useMemo(
    () => options.map(() => createRef()),
    [options],
  );

  useEffect(() => {
    itemRefs[focusedItemIndex]?.current.focus();
  }, [focusedItemIndex, itemRefs]);

  useEffect(() => {
    setSelectedInput(defaultSelectedOption);
  }, [defaultSelectedOption]);

  const handleSelectorClick = () => {
    if (!editMode) {
      return;
    }
    setSelectorOpen(!selectorOpen);
  };
  const handleClickAway = () => {
    setSelectorOpen(false);
  };

  const onSelectionChange = (newValue) => {
    onChange(newValue?.value ?? null);
    selectedVariantProps.onChangeTitle(translate(selectedVariantProps.defaultResetValueTitle) ?? null);
    selectedVariantProps.onChangeInput(translate(selectedVariantProps.defaultResetValuePlaceholder) ?? null);
    if (newValue?.value === 'dropdownField') {
      selectedVariantProps.onChangeDropdown([{ title: translate('customisation.dropdownItem'), value: translate('customisation.dropdownItem') }]);
    }
    setSelectedInput(newValue?.value ?? null);
    setSelectorOpen(false);
  };

  const resetAttribute = () => {
    onChange(null);
    setSelectedInput(null);
    setSelectorOpen(false);
    selectedVariantProps.onChangeTitle(null);
    selectedVariantProps.onChangeInput(null);
    selectedVariantProps.onChangeDropdown(null);
  };

  const onKeyDown = (event, value, func) => {
    if (event.key === 'Enter' && value) {
      func(value);
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      func(true);
      // focus on the first suggestion
      setFocusedItemIndex(0);
    }

    if (event.key === 'Tab') {
      func(false);
      setFocusedItemIndex(null);
    }
  };

  const onKeyDownItem = (event, item) => {
    if (event.key === 'Enter') {
      onSelectionChange(item);
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (focusedItemIndex > 0) {
        setFocusedItemIndex(focusedItemIndex - 1);
      } else {
        // focus on the last suggestion if the user is on the first one
        setFocusedItemIndex(options.length - 1);
      }
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();

      if (focusedItemIndex < options.length - 1) {
        setFocusedItemIndex(focusedItemIndex + 1);
      } else {
        setFocusedItemIndex(0);
      }
    }
  };

  const onKeyDownRemove = (event) => {
    if (event.key === 'Enter') {
      resetAttribute();
    }
  };

  const renderSelectedOption = () => {
    switch (selectedInput) {
      case 'dropdownField':
        return (
          <EditableDropdown
            key={`${inputSelectorId}-type-dropdown`}
            inputSelectorId={`${inputSelectorId}-type-dropdown`}
            titleRules={selectedVariantProps.titleRules}
            placeholderRules={selectedVariantProps.placeholderRules}
            title={selectedVariantProps.title}
            placeholder={selectedVariantProps.placeholder}
            showAsRequired={selectedVariantProps.showAsRequired}
            inputWidth={selectedVariantProps.inputWidth}
            inputHeight={selectedVariantProps.inputHeight}
            defaultResetValueTitle={selectedVariantProps.defaultResetValueTitle}
            defaultResetValuePlaceholder={selectedVariantProps.defaultResetValuePlaceholder}
            titleProps={selectedVariantProps.titleProps}
            placeholderProps={selectedVariantProps.placeholderProps}
            dropdownProps={selectedVariantProps.dropdownProps}
            onChangeTitle={selectedVariantProps.onChangeTitle}
            onChangeInput={selectedVariantProps.onChangeInput}
            onChangeDropdown={selectedVariantProps.onChangeDropdown}
            items={selectedVariantProps.items}
            dropdownItemRules={selectedVariantProps.dropdownItemRules}
            maxCount={selectedVariantProps.dropdownRules?.maxCount}
          />
        );
      case 'inputField':
        return (
          <>
            <EditableTextInput
              key={`${inputSelectorId}-type-input`}
              inputSelectorId={`${inputSelectorId}-type-input`}
              onChangeTitle={selectedVariantProps.onChangeTitle}
              onChangeInput={selectedVariantProps.onChangeInput}
              defaultTitle={selectedVariantProps.title}
              defaultPlaceholder={selectedVariantProps.placeholder}
              defaultResetValueTitle={selectedVariantProps.defaultResetValueTitle}
              defaultResetValuePlaceholder={selectedVariantProps.defaultResetValuePlaceholder}
              showAsRequired={selectedVariantProps.showAsRequired}
              fullWidth
              titleProps={{
                readOnly: selectedVariantProps.titleProps?.readOnly || !editMode,
                textAreaMode: selectedVariantProps.titleProps?.textAreaMode,
                ariaLabel: selectedVariantProps.titleProps?.ariaLabel,
              }}
              placeholderProps={{
                readOnly: selectedVariantProps.placeholderProps?.readOnly || !editMode,
                ariaLabel: selectedVariantProps.placeholderProps?.ariaLabel,
              }}
              titleRules={selectedVariantProps.titleRules}
              placeholderRules={selectedVariantProps.placeholderRules}
            />
          </>
        );
      case 'checkbox':
        return (
          <EditableTextInput
            key={`${inputSelectorId}-type-checkbox`}
            inputSelectorId={`${inputSelectorId}-type-checkbox`}
            onChangeTitle={selectedVariantProps.onChangeTitle}
            onChangeInput={selectedVariantProps.onChangeInput}
            defaultTitle={selectedVariantProps.title}
            defaultPlaceholder={selectedVariantProps.placeholder}
            defaultResetValueTitle={selectedVariantProps.defaultResetValueTitle}
            defaultResetValuePlaceholder={
              selectedVariantProps.defaultResetValuePlaceholder
            }
            showAsRequired={selectedVariantProps.showAsRequired}
            fullWidth
            titleProps={{
              readOnly: selectedVariantProps.titleProps?.readOnly || !editMode,
              textAreaMode: selectedVariantProps.titleProps?.textAreaMode,
            }}
            placeholderProps={{
              readOnly:
                selectedVariantProps.placeholderProps?.readOnly || !editMode,
              checkboxMode: true,
              inputMode: false,
              asLink: false,
              textDisabled: false,
              textAreaMode: true,
            }}
            titleRules={selectedVariantProps.titleRules}
            placeholderRules={selectedVariantProps.placeholderRules}
          />
        );
      default:
        return (
          <>
            <ClickAway
              shouldWeListen={selectorOpen}
              onClickAway={handleClickAway}
            >
              <div className="selector-container">
                <div
                  className="selector"
                  role="button"
                  onClick={handleSelectorClick}
                  tabIndex={0}
                  onKeyDown={(event) => onKeyDown(event, !selectorOpen, setSelectorOpen)}
                >
                  <span className="placeholder">{placeholder}</span>
                </div>
                {selectorOpen && (
                  <ul className="options">
                    {options?.map((option, index) => (
                      <li
                        className="option"
                        key={option.value}
                      >
                        <div
                          ref={itemRefs[index]}
                          role="button"
                          tabIndex={-1}
                          onClick={() => onSelectionChange(option)}
                          onKeyDown={(event) => onKeyDownItem(event, option)}
                        >
                          <TextDiv>{translate(option.label)}</TextDiv>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </ClickAway>
            <style jsx>
              {`

                .selector-container {
                  position: relative;
                  flex: 1;
                }

                .selector {
                  margin-top: 33px;
                  display: flex;
                  flex-direction: row;
                  justify-content: space-between;
                  align-items: center;
                  height: 3rem;
                  width: 100%;
                  max-width: 460px;
                  border: 1px dotted var(--color-mid-grey);
                  padding: 0 14px;
                  border-radius: var(--border-radius);
                  cursor: pointer;
                }

                .placeholder {
                  color: var(--color-mid-grey);
                }

                .options {
                  animation: fadeIn 0.2s ease-in-out;
                  position: absolute;
                  top: 3rem;
                  left: 0;
                  z-index: var(--zindex-dropdown);
                  width: 100%;
                  max-width: 460px;
                  background-color: var(--color-white);
                  border: 1px solid var(--color-mid-grey);
                  border-radius: var(--border-radius);
                  list-style: none;
                  padding: 0;
                }

                .option {
                  padding: 0 14px;
                  height: 3.3rem;
                  display: flex;
                  flex-direction: row;
                  align-items: center;
                  border-bottom: 1px solid var(--color-mid-grey);
                  cursor: pointer;
                }

                .option > div {
                  width: 100%;
                }

                .option:hover,
                .option:focus {
                  background-color: var(--color-light-grey);
                }

                .option:last-child {
                  border-bottom: none;
                }
                
                @keyframes fadeIn {
                  0% {
                    opacity: 0;
                    transform: translateY(-30px);
                  }
                  100% {
                    opacity: 1;
                    transform: translateY(0);
                  }
                }
              `}
            </style>
          </>
        );
    }
  };

  return (
    <div id={inputSelectorId}>
      <div className="custom-input-wrapper">
        {renderSelectedOption()}
        {(editMode && selectedInput) && (
        <div
          className="remove"
          role="button"
          tabIndex={0}
          onClick={() => resetAttribute()}
          onKeyDown={(e) => onKeyDownRemove(e)}
          aria-label="Delete"
        >
          <img src={`${getStaticCdnPath()}/icons/trashcan.svg`} alt="" className="icon-image" />
        </div>
        )}
      </div>
      <style jsx>
        {`
          .custom-input-wrapper {
            position: relative;
          }

          .remove {
            position: absolute;
            right: -16px;
            top: calc(50% + 8px);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            cursor: pointer;
          }
        `}
      </style>
    </div>
  );
};

CustomAttributeSelector.defaultProps = {
  inputSelectorId: '',
  options: [],
  placeholder: '',
  defaultSelectedOption: null,
  onChange: () => {},
  editMode: false,
  selectedVariantProps: {},
};

CustomAttributeSelector.propTypes = {
  inputSelectorId: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  placeholder: PropTypes.string,
  defaultSelectedOption: PropTypes.string,
  onChange: PropTypes.func,
  editMode: PropTypes.bool,
  selectedVariantProps: PropTypes.shape({
    title: PropTypes.string,
    showAsRequired: PropTypes.bool,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        value: PropTypes.string,
      }),
    ),
    placeholder: PropTypes.string,
    maxDropdownItemslength: PropTypes.number,
    inputWidth: PropTypes.number,
    inputHeight: PropTypes.number,
    onChangeTitle: PropTypes.func,
    onChangeInput: PropTypes.func,
    onChangeDropdown: PropTypes.func,
    primaryColor: PropTypes.string,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,
    defaultResetValueTitle: PropTypes.string,
    defaultResetValuePlaceholder: PropTypes.string,
    titleProps: PropTypes.shape({
      readOnly: PropTypes.bool,
      textAreaMode: PropTypes.bool,
      ariaLabel: PropTypes.string,
    }),
    placeholderProps: PropTypes.shape({
      readOnly: PropTypes.bool,
      textAreaMode: PropTypes.bool,
      minHeight: PropTypes.string,
      ariaLabel: PropTypes.string,
    }),
    dropdownProps: PropTypes.shape({
      readOnly: PropTypes.bool,
      ariaLabel: PropTypes.string,
    }),
    titleRules: PropTypes.shape({
      onSubmit: PropTypes.arrayOf(PropTypes.string),
      onBlurValidationRules: PropTypes.arrayOf(PropTypes.string),
      onChange: PropTypes.arrayOf(PropTypes.string),
      requiredError: PropTypes.string,
      submitErrors: PropTypes.arrayOf(PropTypes.string),
      onChangeErrors: PropTypes.arrayOf(PropTypes.string),
      onBlurValidationErrors: PropTypes.arrayOf(PropTypes.string),
      minLength: PropTypes.number,
      maxLength: PropTypes.number,
      tooShortError: PropTypes.string,
      tooLongError: PropTypes.string,
    }),
    placeholderRules: PropTypes.shape({
      onSubmit: PropTypes.arrayOf(PropTypes.string),
      onBlurValidationRules: PropTypes.arrayOf(PropTypes.string),
      onChange: PropTypes.arrayOf(PropTypes.string),
      requiredError: PropTypes.string,
      submitErrors: PropTypes.arrayOf(PropTypes.string),
      onChangeErrors: PropTypes.arrayOf(PropTypes.string),
      onBlurValidationErrors: PropTypes.arrayOf(PropTypes.string),
      minLength: PropTypes.number,
      maxLength: PropTypes.number,
      tooShortError: PropTypes.string,
      tooLongError: PropTypes.string,
    }),
    dropdownRules: PropTypes.shape({
      maxCount: PropTypes.number,
    }),
    dropdownItemRules: PropTypes.shape({
      onSubmit: PropTypes.arrayOf(PropTypes.string),
      onBlurValidationRules: PropTypes.arrayOf(PropTypes.string),
      onChange: PropTypes.arrayOf(PropTypes.string),
      requiredError: PropTypes.string,
      submitErrors: PropTypes.arrayOf(PropTypes.string),
      onChangeErrors: PropTypes.arrayOf(PropTypes.string),
      onBlurValidationErrors: PropTypes.arrayOf(PropTypes.string),
      minLength: PropTypes.number,
      maxLength: PropTypes.number,
      tooShortError: PropTypes.string,
      tooLongError: PropTypes.string,
    }),
  }),
};

CustomAttributeSelector.displayName = 'CustomAttributeSelector';

export default CustomAttributeSelector;
